<script setup>
import { computed } from 'vue';

const props = defineProps({
  name: { type: String, required: true },
  category: { type: String, default: 'other' },
  defaultPrice: { type: [Number, String], default: null },
  unit: { type: String, default: '' },
  currency: { type: String, default: 'EUR' },
});

const categoryConfig = computed(() => {
  const configs = {
    space: {
      icon: 'i-lucide-orbit',
      label: 'Space',
      color: 'bg-n-violet-3 text-n-violet-11',
    },
    staff: {
      icon: 'i-lucide-users',
      label: 'Staff',
      color: 'bg-n-blue-3 text-n-blue-11',
    },
    catering: {
      icon: 'i-lucide-utensils',
      label: 'Catering',
      color: 'bg-n-amber-3 text-n-amber-11',
    },
    av: {
      icon: 'i-lucide-monitor',
      label: 'AV',
      color: 'bg-n-cyan-3 text-n-cyan-11',
    },
    add_on: {
      icon: 'i-lucide-plus-circle',
      label: 'Add-on',
      color: 'bg-n-green-3 text-n-green-11',
    },
    other: {
      icon: 'i-lucide-box',
      label: 'Other',
      color: 'bg-n-slate-3 text-n-slate-10',
    },
  };
  return configs[props.category] || configs.other;
});

const formattedPrice = computed(() => {
  if (props.defaultPrice == null) return null;
  const num = Number(props.defaultPrice);
  if (Number.isNaN(num)) return props.defaultPrice;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: props.currency || 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
});

const unitLabel = computed(() => {
  const labels = {
    per_day: '/day',
    per_hour: '/hour',
    per_guest: '/guest',
    per_hour_per_guest: '/hour · guest',
    per_quantity: '/qty',
    per_event: '/event',
  };
  return labels[props.unit] || '';
});
</script>

<template>
  <div
    class="inline-flex items-center gap-2.5 p-2.5 rounded-lg bg-n-solid-2 ring-1 ring-n-weak max-w-sm"
  >
    <!-- Category badge -->
    <span
      class="flex items-center justify-center size-7 rounded-md shrink-0"
      :class="categoryConfig.color"
    >
      <span class="size-4" :class="categoryConfig.icon" />
    </span>

    <!-- Info -->
    <div class="flex-1 min-w-0">
      <p class="text-sm font-medium text-n-slate-12 truncate">{{ name }}</p>
      <p class="text-xs text-n-slate-10">{{ categoryConfig.label }}</p>
    </div>

    <!-- Price -->
    <div v-if="formattedPrice" class="text-right shrink-0">
      <p class="text-sm font-semibold text-n-slate-12">{{ formattedPrice }}</p>
      <p v-if="unitLabel" class="text-xs text-n-slate-10">{{ unitLabel }}</p>
    </div>
  </div>
</template>
